<script setup lang="ts">
import { CategoryType, type ProductDetails } from "mws-ts-v2-sdk";

import { OG_IMAGE_SIZE } from "@/constants/metaTags";
import { MediaContentType } from "~/types/Catalog";

const { product } = defineProps<{
  product: ProductDetails;
}>();

const i18n = useI18n();
const platformStore = usePlatformStore();
const platformName = platformStore.getPlatformName;

const title = computed(() => {
  const matchName = product?.details?.Match;
  const productName = product?.name;

  if (!productName) return platformName;

  if (matchName) {
    return `${productName} | ${matchName} | ${platformName}`;
  }

  return `${productName} | ${platformName}`;
});

const isFootballShirt = computed(() => product?.type === "football_shirt");

const productStarted = computed(
  () => product && product.startDate <= new Date(),
);

const isProductLive = computed(
  () => productStarted.value && !product?.finished,
);

const productLiveStatus = computed(() =>
  i18n.t(
    `product.meta.footballShirts.biddingProduct.${
      isProductLive.value ? "onlineLiveStatus" : "offlineLiveStatus"
    }`,
  ),
);
const productGender = computed(() => {
  const gender = filterCategory(product, CategoryType.Gender)?.name;
  if (!gender) return undefined;

  return i18n.t(`common.genderPlural.${gender.toLowerCase()}`);
});
const productAthlete = computed(
  () => filterCategory(product, CategoryType.Athlete)?.name,
);
const productLeague = computed(
  () => filterCategory(product, CategoryType.League)?.name,
);
const productLabel = computed(() => {
  if (!product) return undefined;

  const getLabel = (text: string) =>
    product.labels.find((label) => label.text === text);

  const returnLabel = (label: string) =>
    translateWithFallback(i18n, `product.labels.${label}`, label);

  const wornLabel = getLabel("Worn");
  if (wornLabel) return returnLabel(wornLabel.text);

  const signedLabel = getLabel("Signed");
  if (signedLabel) return returnLabel(signedLabel.text);

  const issuedLabel = getLabel("Issued");
  if (issuedLabel) return returnLabel(issuedLabel.text);

  return undefined;
});
const productSize = computed(() => product?.details.size);
const productSeason = computed(() => product?.details.season);
const productEvent = computed(() => product?.details.match);
const productDate = computed(() => product?.details.date);

const shouldShowFootballShirtDescription = computed(
  () =>
    isFootballShirt.value &&
    productGender.value &&
    !!productAthlete.value &&
    !!productLabel.value &&
    !!productSize.value,
);

const descriptionContent = computed(() => {
  const fallbackContent = `See ${product?.name}`;

  if (shouldShowFootballShirtDescription.value)
    return translateWithFallback(
      i18n,
      "product.meta.footballShirts.biddingProduct.description",
      fallbackContent,
      {
        liveStatus: productLiveStatus.value,
        gender: productGender.value,
        athlete: productAthlete.value,
        label: productLabel.value,
        size: productSize.value,
        date: productDate.value,
        event: productEvent.value,
        league: productLeague.value,
        season: productSeason.value,
        platformName,
      },
    );

  return fallbackContent;
});

const productImageUrl = computed(() => {
  const imageUrl = product?.media.find(
    (m) => m.type === MediaContentType.Image && !!m.url,
  )?.url;

  return imageUrl
    ? `${imageUrl}?w=${OG_IMAGE_SIZE}&h=${OG_IMAGE_SIZE}`
    : undefined;
});

useProductSchemaOrg(() => product);
</script>

<template>
  <LayoutMetaTags
    :meta-tags="{
      title,
      description: descriptionContent,
      og_image: productImageUrl ?? undefined,
    }"
  />
</template>
